import React from 'react';
import { AlertCircle, CheckCircle, HelpCircle } from 'lucide-react';

export type MessageDialogTone = 'info' | 'success' | 'error';

export type MessageDialogOpen =
  | null
  | {
      variant: 'alert';
      title: string;
      message: string;
      tone?: MessageDialogTone;
    }
  | {
      variant: 'confirm';
      title: string;
      message: string;
      tone?: 'danger' | 'default';
      confirmLabel?: string;
      cancelLabel?: string;
      onConfirm: () => void;
      /** Optional third action (e.g. Re-run) shown between Cancel and Confirm. */
      secondaryLabel?: string;
      onSecondary?: () => void;
    };

interface MessageDialogProps {
  state: MessageDialogOpen;
  onClose: () => void;
}

const MessageDialog: React.FC<MessageDialogProps> = ({ state, onClose }) => {
  if (!state) return null;

  const isAlert = state.variant === 'alert';
  const tone = isAlert ? state.tone ?? 'info' : state.tone === 'danger' ? 'error' : 'info';

  const iconWrap =
    tone === 'success'
      ? 'bg-emerald-50 text-emerald-700'
      : tone === 'error'
        ? 'bg-red-50 text-red-700'
        : 'bg-blue-50 text-blue-700';

  const Icon = tone === 'success' ? CheckCircle : tone === 'error' ? AlertCircle : HelpCircle;

  const handleConfirm = () => {
    if (state.variant === 'confirm') {
      state.onConfirm();
    }
    onClose();
  };

  const handleSecondary = () => {
    if (state.variant === 'confirm' && state.onSecondary) {
      state.onSecondary();
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[12000] flex items-center justify-center p-4 bg-black/45"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="message-dialog-title"
      aria-describedby="message-dialog-desc"
      onClick={(e) => {
        if (e.target === e.currentTarget && isAlert) onClose();
      }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-neutral-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5">
          <div className={`shrink-0 rounded-full p-2 ${iconWrap}`}>
            <Icon className="h-6 w-6" aria-hidden />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h2 id="message-dialog-title" className="text-lg font-semibold text-neutral-900">
              {state.title}
            </h2>
            <p id="message-dialog-desc" className="mt-2 text-sm text-neutral-600 whitespace-pre-wrap">
              {state.message}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 bg-neutral-50 border-t border-neutral-200">
          {!isAlert ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-neutral-800 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
              >
                {state.cancelLabel ?? 'Cancel'}
              </button>
              {state.secondaryLabel && state.onSecondary ? (
                <button
                  type="button"
                  onClick={handleSecondary}
                  className="px-4 py-2 text-sm font-medium text-neutral-800 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
                >
                  {state.secondaryLabel}
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                  state.tone === 'danger'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-neutral-900 hover:bg-neutral-800'
                }`}
              >
                {state.confirmLabel ?? 'Confirm'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-neutral-900 rounded-lg hover:bg-neutral-800"
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageDialog;
